import { useCallback, useState, useSyncExternalStore } from "react";
import { useLocation } from "wouter";
import type { Priority, Task, TaskId } from "../../../../shared/types";
import { getMonthYear } from "../../../../shared/types/date-helpers";
import { CalendarNavigation } from "../CalendarNavigation";
import { TaskQuickViewModal } from "../dialogs/TaskQuickViewModal";
import { AddTaskInlineDialog } from "../dialogs/AddTaskInlineDialog";
import { addTask } from "../../../tasks/store/task-store";
import { CalendarHelpButton } from "../week-grid/WeekGridDayHeaders";
import { MobileCalendarContent } from "./MobileCalendarContent";
import { MobileEditPanel } from "./MobileEditPanel";
import { RecurrenceDialog } from "../dialogs/RecurrenceDialog";
import { useHolidays } from "../../../../shared/hooks/use-holidays";
import { showHolidaysStore } from "../../../../hooks/useAppState";

export type { MobileCalendarViewProps } from "./mobile-calendar-view-types";
import type { MobileCalendarViewProps } from "./mobile-calendar-view-types";

export function MobileCalendarView({
  nav,
  today,
  displayDays,
  selectedDate,
  categoryDotsForDate,
  eventsByDate,
  tasksByDate,
  selectedEvents,
  selectedTasks,
  now,
  showAddForm,
  formStartTime,
  formEndTime,
  activeCategory,
  onHandleEventClick,
  onHandleMobileSlotClick,
  onAddEvent,
  onAddEventFull,
  onDeleteEvent,
  onUpdateEvent,
  onSetShowAddForm,
  onSetFormStartTime,
  onSetFormEndTime,
  onCloseAddForm,
  editing,
  pendingRecurrence,
  pendingResync,
  recurrenceToast,
  onHandleRecurrenceChoice,
  onHandleResyncChoice,
  onSetPendingRecurrence,
  readOnly,
  onToggleReadOnly,
}: MobileCalendarViewProps) {
  const [, navigate] = useLocation();
  const holidaysEnabled = useSyncExternalStore(showHolidaysStore.subscribe, showHolidaysStore.get);
  const allHolidays = useHolidays(displayDays);
  const holidays = holidaysEnabled ? allHolidays : undefined;
  const [quickViewTaskId, setQuickViewTaskId] = useState<TaskId | null>(null);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [headerCompact, setHeaderCompact] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("palnia_mobile_header_compact") === "true";
  });

  const toggleHeaderCompact = useCallback(() => {
    setHeaderCompact((v) => {
      const next = !v;
      localStorage.setItem("palnia_mobile_header_compact", String(next));
      return next;
    });
  }, []);

  const handleTaskClick = useCallback((task: Task) => {
    setQuickViewTaskId(task.id);
  }, []);

  const handleAddTask = useCallback(() => {
    setShowAddTaskDialog(true);
  }, []);

  const handleDayClick = useCallback((date: string) => {
    // Si on re-clique sur le jour déjà sélectionné en mode mois → ajouter tâche
    if (nav.calendarMode === "month" && selectedDate === date) {
      setShowAddTaskDialog(true);
      return;
    }
    nav.handleDayClick(date);
  }, [nav, selectedDate]);

  const handleAddTaskConfirm = useCallback(
    (title: string, category: string, priority: Priority) => {
      const date = nav.calendarMode === "day" ? nav.currentDate : (selectedDate ?? nav.currentDate);
      addTask(title, category, date, priority);
      setShowAddTaskDialog(false);
    },
    [nav, selectedDate],
  );

  const effectiveDate = selectedDate ?? nav.currentDate;

  return (
    <>
      <div className="flex flex-col gap-2 md:hidden h-[calc(100dvh-4rem)] overflow-hidden">
        {/* Navigation — pliable */}
        {!headerCompact && (
          <div className="shrink-0">
            <CalendarNavigation
              currentDate={nav.currentDate}
              calendarMode={nav.calendarMode}
              onPrev={nav.goToPrev}
              onNext={nav.goToNext}
              onToday={nav.goToToday}
              onGoToDate={nav.goToDate}
              onToggleFullscreen={() => navigate("/calendar/fullscreen")}
              onModeChange={nav.setCalendarMode}
              readOnly={readOnly}
              onToggleReadOnly={onToggleReadOnly}
              onToggleCompact={toggleHeaderCompact}
            />
          </div>
        )}
        {headerCompact && (
          <div className="shrink-0 flex items-center justify-between px-3 py-0.5">
            <button
              type="button"
              onClick={toggleHeaderCompact}
              className="flex-1 text-left text-[11px] text-ink-faint font-medium active:text-ink-muted transition-colors"
              aria-label="Afficher la navigation"
            >
              {getMonthYear(nav.currentDate)}
            </button>
            <div className="flex items-center gap-0.5">
              <CalendarHelpButton />
              <button
                type="button"
                onClick={toggleHeaderCompact}
                className="w-6 h-6 flex items-center justify-center rounded text-accent/40 active:text-accent transition-colors"
                aria-label="Afficher la navigation"
              >
                <svg className="w-3.5 h-3.5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Contenu scrollable */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {effectiveDate && (
            <MobileCalendarContent
              selectedDate={effectiveDate}
              currentDate={nav.currentDate}
              monthDays={nav.monthDays}
              displayDays={displayDays}
              calendarMode={nav.calendarMode}
              today={today}
              now={now}
              selectedEvents={selectedEvents}
              selectedTasks={selectedTasks}
              showAddForm={showAddForm}
              formStartTime={formStartTime}
              formEndTime={formEndTime}
              activeCategory={activeCategory}
              eventsByDate={eventsByDate}
              tasksByDate={tasksByDate}
              categoryDotsForDate={categoryDotsForDate}
              onHandleEventClick={onHandleEventClick}
              onHandleMobileSlotClick={readOnly ? undefined : onHandleMobileSlotClick}
              onGoToNext={nav.goToNext}
              onGoToPrev={nav.goToPrev}
              onAddEvent={onAddEvent}
              onAddEventFull={onAddEventFull}
              onDeleteEvent={readOnly ? undefined : onDeleteEvent}
              onUpdateEvent={readOnly ? undefined : onUpdateEvent}
              onSetShowAddForm={onSetShowAddForm}
              onSetFormStartTime={onSetFormStartTime}
              onSetFormEndTime={onSetFormEndTime}
              onCloseAddForm={onCloseAddForm}
              onTaskClick={handleTaskClick}
              onDayClick={handleDayClick}
              onAddTask={readOnly ? undefined : handleAddTask}
              holidays={holidays}
            />
          )}
        </div>
      </div>

      {/* Modales et panels - cachés sur desktop */}
      <div className="md:hidden">
        {quickViewTaskId && (
          <TaskQuickViewModal
            taskId={quickViewTaskId}
            onClose={() => setQuickViewTaskId(null)}
          />
        )}
        <AddTaskInlineDialog
          open={showAddTaskDialog}
          defaultCategory={activeCategory}
          onConfirm={handleAddTaskConfirm}
          onCancel={() => setShowAddTaskDialog(false)}
        />
        <MobileEditPanel
          {...editing}
          activeCategory={activeCategory}
          onDeleteEvent={readOnly ? undefined : onDeleteEvent}
          onUpdateEvent={readOnly ? undefined : onUpdateEvent}
          onAddEventFull={readOnly ? undefined : onAddEventFull}
          onAddEvent={readOnly ? undefined : onAddEvent}
          readOnly={readOnly}
        />
        <RecurrenceDialog
          pendingRecurrence={pendingRecurrence}
          pendingResync={pendingResync}
          recurrenceToast={recurrenceToast}
          onRecurrenceChoice={onHandleRecurrenceChoice}
          onResyncChoice={onHandleResyncChoice}
          onClosePendingRecurrence={() => onSetPendingRecurrence(null)}
          onClosePendingResync={() => editing.onSetPendingResync(null)}
          onDismissToast={() => editing.onSetRecurrenceToast(null)}
        />
      </div>
    </>
  );
}
